import type { HTMLAttributes, SVGProps } from "react";
import { SocialBrandMark, type SocialBrandChannel } from "../SocialBrandMark/SocialBrandMark";
import { resolveInstagramSourceChannel, resolveLeadSource, type InstagramSourceChannel, type LeadSourceKind } from "./leadSource";
import styles from "./LeadSourceBadge.module.css";

type SourceIconProps = SVGProps<SVGSVGElement>;

function YouTubeIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M21.4 7.1a2.9 2.9 0 0 0-2-2C17.7 4.6 12 4.6 12 4.6s-5.7 0-7.4.5a2.9 2.9 0 0 0-2 2A30 30 0 0 0 2.1 12a30 30 0 0 0 .5 4.9 2.9 2.9 0 0 0 2 2c1.7.5 7.4.5 7.4.5s5.7 0 7.4-.5a2.9 2.9 0 0 0 2-2 30 30 0 0 0 .5-4.9 30 30 0 0 0-.5-4.9ZM10 15.2V8.8l5.5 3.2-5.5 3.2Z" /></svg>;
}

function WalkInIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M4 10h16l-1.2-5.2H5.2L4 10Z" /><path d="M5.5 10v9h13v-9M9 19v-5h6v5" /><path d="M4 10c0 1 .8 1.8 1.8 1.8S7.5 11 7.5 10c0 1 .8 1.8 1.8 1.8S11 11 11 10c0 1 .8 1.8 1.8 1.8s1.7-.8 1.7-1.8c0 1 .8 1.8 1.8 1.8S20 11 20 10" /></svg>;
}

function ReferralIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="8" cy="8" r="2.5" /><path d="M3.8 17.5c.5-2.4 1.9-3.8 4.2-3.8 1.4 0 2.5.5 3.2 1.4" /><circle cx="16.5" cy="14.5" r="2" /><path d="M13.1 20c.4-1.9 1.5-3 3.4-3 1.8 0 3 1.1 3.4 3M13 5h6m-2.2-2.2L19 5l-2.2 2.2" /></svg>;
}

function ManualIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M9 5H6.5A1.5 1.5 0 0 0 5 6.5v12A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V16" /><rect x="8.5" y="3" width="7" height="4" rx="1.5" /><path d="m11 16 1.1-3.3 5.7-5.7 2.2 2.2-5.7 5.7L11 16Z" /></svg>;
}

function PhoneIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M7.1 4.4 9.3 8 7.8 9.5c1.4 3 3.7 5.3 6.7 6.7l1.5-1.5 3.6 2.2c.4.2.5.7.3 1.1-.7 1.3-1.9 2-3.5 2C9.5 19.2 4.8 14.5 4 7.6 3.8 6 4.5 4.8 5.8 4.1c.5-.2 1-.1 1.3.3Z" /></svg>;
}

function WebsiteIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="8.5" /><path d="M3.8 12h16.4M12 3.5c2.2 2.3 3.3 5.1 3.3 8.5S14.2 18.2 12 20.5C9.8 18.2 8.7 15.4 8.7 12S9.8 5.8 12 3.5Z" /></svg>;
}

function OtherIcon(props: SourceIconProps) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-11Z" /><path d="M4 13h4l1.5 2h5l1.5-2h4" /></svg>;
}

const ICONS: Record<Exclude<LeadSourceKind, SocialBrandChannel>, React.ReactNode> = {
  youtube: <YouTubeIcon focusable="false" aria-hidden="true" />,
  "walk-in": <WalkInIcon focusable="false" aria-hidden="true" />,
  referral: <ReferralIcon focusable="false" aria-hidden="true" />,
  manual: <ManualIcon focusable="false" aria-hidden="true" />,
  phone: <PhoneIcon focusable="false" aria-hidden="true" />,
  website: <WebsiteIcon focusable="false" aria-hidden="true" />,
  other: <OtherIcon focusable="false" aria-hidden="true" />,
};

function isSocialBrandChannel(kind: LeadSourceKind): kind is SocialBrandChannel {
  return kind === "instagram" || kind === "facebook" || kind === "whatsapp";
}

function InstagramChannelMarker({ channel }: { channel: InstagramSourceChannel }) {
  return (
    <span className={styles.channelMarker} data-instagram-channel={channel} aria-hidden="true">
      {channel === "comment"
        ? <svg viewBox="0 0 12 12" fill="none"><path d="M2 2.3h8v5.5H5.1L2.4 10V7.8H2V2.3Z" /></svg>
        : <svg viewBox="0 0 12 12" fill="none"><path d="m1.7 5.8 8.6-3.7-2.7 7.8-2-2-1.5 1.2.2-2.5-2.6-.8Z" /><path d="m4.3 6.6 3.5-2.2" /></svg>}
    </span>
  );
}

function sourceIconFor(kind: LeadSourceKind): React.ReactNode {
  if (isSocialBrandChannel(kind)) return null;
  return ICONS[kind];
}

export interface LeadSourceIconProps extends HTMLAttributes<HTMLSpanElement> {
  source: unknown;
  size?: "small" | "medium";
  decorative?: boolean;
  imageAlt?: string;
}

export function LeadSourceIcon({
  source,
  size = "medium",
  decorative = true,
  imageAlt,
  className = "",
  ...props
}: LeadSourceIconProps) {
  const descriptor = resolveLeadSource(source);
  const socialChannel = isSocialBrandChannel(descriptor.kind) ? descriptor.kind : null;
  const instagramChannel = resolveInstagramSourceChannel(source);
  const meaningfulImageAlt = socialChannel ? imageAlt?.trim() || "" : "";
  const icon = sourceIconFor(descriptor.kind);
  return (
    <span
      {...props}
      className={`${styles.icon} ${styles[descriptor.kind]} ${styles[size]} ${className}`}
      data-source-kind={descriptor.kind}
      data-instagram-channel={instagramChannel || undefined}
      aria-hidden={meaningfulImageAlt ? undefined : decorative || undefined}
      aria-label={meaningfulImageAlt || decorative ? undefined : `${descriptor.label} source`}
      role={meaningfulImageAlt || decorative ? undefined : "img"}
    >
      {socialChannel ? <SocialBrandMark channel={socialChannel} alt={meaningfulImageAlt} /> : icon}
      {instagramChannel ? <InstagramChannelMarker channel={instagramChannel} /> : null}
    </span>
  );
}

export interface LeadSourceBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  source: unknown;
  label?: string;
  size?: "small" | "medium";
}

export function LeadSourceBadge({
  source,
  label,
  size = "medium",
  className = "",
  ...props
}: LeadSourceBadgeProps) {
  const descriptor = resolveLeadSource(source);
  const visibleLabel = label?.trim() || descriptor.label;
  return (
    <span
      {...props}
      className={`${styles.badge} ${styles[descriptor.kind]} ${styles[size]} ${className}`}
      data-source-kind={descriptor.kind}
      title={props.title || visibleLabel}
    >
      <LeadSourceIcon source={source} size="small" />
      <span className={styles.label}>{visibleLabel}</span>
    </span>
  );
}
